import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Tabs, TabsContent } from '@frontend/shadcn/components/ui/tabs';
import { TooltipProvider } from '@frontend/shadcn/components/ui/tooltip';
import { useAuth } from '@frontend/context/authContext';
import { SlidersIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@frontend/shadcn/components/ui/sheet';
import { z } from 'zod';
import { useGetCurrentClient } from '@frontend/app/hooks/client/useGetCurrentClient';
import { isEqual } from 'lodash';
import { ClientFeature } from '@frontend/app/constants/clientFeatures';
import { useClientFeatureEnabled } from '@frontend/app/hooks';
import { useSearchMembers, useMembersByIds, useMergeMembersData } from './hooks';
import { useSummaryData } from './hooks/useSummaryData';
import { useSummaryTab } from './hooks/useSummaryTab';
import { HeaderTabsList, HeaderTabsTrigger } from '../components/header';
import { InstagramAccount, MetaAdAccount, SocialNetwork, SocialPostType } from '../../../gql/social/graphql';
import { useTabNavigation } from './hooks/useTabNavigation';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import { useAnalyticsParams } from './hooks/useAnalyticsParams';
import { QueryVariables, DateType, ViewMode, MetricType } from './types';
import { calculateDatesFromType } from '../utils/dates';
import { SummaryTab } from './components/tabs/SummaryTab';
import { PostsTab } from './components/tabs/PostsTab';
import { MembersTab } from './components/tabs/MembersTab';
import { NetworksTab } from './components/tabs/NetworksTab';
import { AnalyticsHeader } from './components/AnalyticsHeader';
import { FiltersForm, filtersFormSchema, FiltersFormData } from './components/filters-form';
import { DateRangeForm, DateRangeFormData } from './components/date-range-form';
import { useFilterFormData } from './components/filters-form/hooks/useFilterFormData';
import { SocialAnalyticsFilterBadges } from './components/filter-badges';
import {
  useGroupBadges,
  useIncludeEstimates,
  useNetworkBadges,
  usePostTypeBadges,
  useProjectBadges,
  useSegmentBadge,
  useMemberBadges,
  usePostDateIntervalEnabledBadge,
  // useIncludeUnassignedBadge,
} from './components/filter-badges/hooks';
import { defaultColumns } from './components/table/config/defaultColumns';
import { Spinner } from './components/ui/spinner';
import { generateIncludeOptions } from './utils/columnUtils';
import { usePostsData } from './hooks/usePostsData';
import { defaultVisiblePostColumnIds } from './components/table/config/defaultPostTableColumns';
import { useNetworksData } from './hooks/useNetworksData';
import { defaultNetworksTableColumns } from './components/table/config/defaultNetworksTableColumns';
import { useMembersTab } from './hooks/useMembersTab';
import { usePostsTab } from './hooks/usePostsTab';
import { useNetworksTab } from './hooks/useNetworksTab';
import { useMetaAdAccounts } from './hooks/useMetaAdAccounts';
import { usePostDetails } from './hooks/usePostDetails';

const defaultPostTypes: SocialPostType[] = [];
const defaultNetworks: SocialNetwork[] = [];

const DEFAULT_VARIABLES: Omit<QueryVariables, 'clientId'> = {
  dateType: 'last6Months' as DateType,
  postTypes: defaultPostTypes,
  includeEstimates: true,
  networks: defaultNetworks,
  projectIds: [],
  groupIds: [],
  memberIds: [],
  segments: [],
  viewMode: 'cards' as ViewMode,
  postDateIntervalEnabled: false,
};

export const SocialAnalytics = () => {
  const { tab } = useParams<{ tab: string }>();
  const [selectedPostColumns, setSelectedPostColumns] = useState(defaultVisiblePostColumnIds);
  const [selectedNetworksColumns, setSelectedNetworksColumns] = useState(defaultNetworksTableColumns);
  const { clientInfo } = useAuth();
  const { client } = useGetCurrentClient();
  const clientId = clientInfo?.id;
  const clientCreatedDate = client?.createdDate;
  const location = useLocation();
  const { handleTabChange } = useTabNavigation();
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);
  const [searchMemberTerm, setMemberSearchTerm] = React.useState<string>('');
  const [selectedPostMetric, setSelectedPostMetric] = useState<MetricType>('engagements');
  const [selectedCreatorMetric, setSelectedCreatorMetric] = useState<MetricType>('engagements');

  const { queryVariables, handleApplyFilters, handleDateRangeSubmit, updateUrlParams } = useAnalyticsParams({
    clientId,
    defaultVariables: DEFAULT_VARIABLES,
    clientCreatedDate,
  });

  const { projects, groups, isFetchingProjects, isFetchingGroups, activeProjectIds } = useFilterFormData();

  const memberIds = queryVariables?.memberIds?.map((id: string) => Number(id)) || [];
  // Query for search term
  const { data: searchData, loading: searchLoading, error: searchError } = useSearchMembers(searchMemberTerm);
  // Separate query for memberIds
  const { data: memberIdsData, loading: memberIdsLoading, error: memberIdsError } = useMembersByIds(memberIds);
  // Combine and deduplicate the results
  const {
    data: { members = [] },
    loading: isFetchingMembers,
  } = useMergeMembersData(searchData, searchLoading, searchError, memberIdsData, memberIdsLoading, memberIdsError);

  // Convert members to match the updated Member type with string id
  const mappedMembers = useMemo(
    () =>
      members.map((member) => ({
        ...member,
        id: String(member.id),
        name: member.name ?? undefined,
        email: member.email ?? undefined,
        profilePicture: member.profilePicture ?? undefined,
      })),
    [members],
  );

  const { projectBadgesProps } = useProjectBadges({ projects, projectIds: queryVariables.projectIds ?? [] });
  const { groupBadgesProps } = useGroupBadges({ groups, groupIds: queryVariables.groupIds ?? [] });
  const { networkBadgesProps } = useNetworkBadges({ networks: queryVariables.networks ?? [] });
  const { postTypeBadgesProps } = usePostTypeBadges({ postTypes: queryVariables.postTypes ?? [] });
  const { includeEstimatesBadgeProps } = useIncludeEstimates({ includeEstimates: queryVariables.includeEstimates });
  const { segmentBadgeProps } = useSegmentBadge({ segments: queryVariables.segments ?? [] });
  const { memberBadgesProps } = useMemberBadges({ memberIds: queryVariables.memberIds ?? [], members: mappedMembers });
  const { postDateIntervalEnabledBadgeProps } = usePostDateIntervalEnabledBadge({
    postDateIntervalEnabled: queryVariables.postDateIntervalEnabled,
  });

  // Initialize URL params if they don't exist
  React.useEffect(() => {
    if (!location.search && clientId && !isFetchingProjects) {
      const dates = calculateDatesFromType(
        DEFAULT_VARIABLES.dateType,
        clientCreatedDate
          ? (() => {
              const d = new Date(clientCreatedDate);
              d.setUTCHours(0, 0, 0, 0);
              return d;
            })()
          : undefined,
      );
      const initialVariables = {
        ...DEFAULT_VARIABLES,
        clientId,
        startDate: dates.startDate.toISOString().split('T')[0],
        endDate: dates.endDate.toISOString().split('T')[0],
        projectIds: activeProjectIds,
      };
      updateUrlParams(initialVariables);
    }
  }, [location.search, updateUrlParams, clientId, clientCreatedDate, activeProjectIds, isFetchingProjects]);

  const contentPreviewBoost = useClientFeatureEnabled(ClientFeature.CONTENT_PREVIEW_BOOST);

  const {
    data: postsData,
    fetching: fetchingPosts,
    handleRefresh: handleRefreshPosts,
    updateLocalAssignment: updatePostsDataAssignment,
  } = usePostsData({
    clientId,
    tab,
    queryVariables,
    projects,
    groups,
    hasBoost: contentPreviewBoost,
  });

  const {
    data: networksData,
    fetching: fetchingNetworks,
    handleRefresh: handleRefreshNetworks,
  } = useNetworksData({
    clientId,
    tab,
    queryVariables,
  });

  const { data: metaAdAccountsData } = useMetaAdAccounts({
    clientId,
    pause: !contentPreviewBoost,
  });

  const { data: postDetailsData, updateLocalAssignment: updatePostDetailAssignment } = usePostDetails({
    clientId,
    postId: queryVariables.postId,
    queryVariables,
    pause: !queryVariables.postId,
    projects,
    groups,
  });

  const postDetails = useMemo(() => {
    return postDetailsData?.client?.socialAnalytics?.posts?.results?.find((p) => !!p) || null;
  }, [postDetailsData]);

  // Create a combined updateLocalAssignment function
  const combinedUpdateLocalAssignment = useCallback(
    (update: Parameters<typeof updatePostsDataAssignment>[0]) => {
      updatePostsDataAssignment(update);
      if (updatePostDetailAssignment) {
        updatePostDetailAssignment(update);
      }
    },
    [updatePostsDataAssignment, updatePostDetailAssignment],
  );

  // Move the analytics data hook before its usage
  const includeOptions = useMemo(() => generateIncludeOptions(defaultColumns, selectedColumns), [selectedColumns]);
  const {
    data,
    fetching,
    refetching,
    handleRefresh: handleRefreshMembers,
  } = useAnalyticsData({
    clientId,
    tab,
    queryVariables,
    includeOptions,
  });

  // Add summary data fetching
  const {
    data: summaryData,
    fetching: fetchingSummary,
    handleRefresh: handleRefreshSummary,
    topPostsData,
    topPostsFetching,
    topCreatorsData,
    topCreatorsFetching,
  } = useSummaryData({
    clientId,
    tab,
    queryVariables,
  });

  // Select top 5 posts by selected metric for TopPerformersRow
  const topPosts = useMemo(() => {
    if (!topPostsData) return [];
    return [...topPostsData]
      .sort((a, b) => {
        const aValue = a.total?.[selectedPostMetric] || 0;
        const bValue = b.total?.[selectedPostMetric] || 0;
        return bValue - aValue;
      })
      .slice(0, 5);
  }, [topPostsData, selectedPostMetric]);

  // Select top 5 creators by selected metric for TopCreatorsRow
  const topCreators = useMemo(() => {
    if (!topCreatorsData) return [];
    return [...topCreatorsData]
      .sort((a, b) => {
        const aValue = a.total?.[selectedCreatorMetric] || 0;
        const bValue = b.total?.[selectedCreatorMetric] || 0;
        return bValue - aValue;
      })
      .slice(0, 5);
  }, [topCreatorsData, selectedCreatorMetric]);

  const { summaryTabProps } = useSummaryTab({
    data: summaryData,
    fetching: fetchingSummary,
    startDate: queryVariables.startDate,
    endDate: queryVariables.endDate,
    dateType: queryVariables.dateType,
    clientId,
    topPosts,
    topPostsFetching,
    topCreators,
    topCreatorsFetching,
    selectedPostMetric,
    setSelectedPostMetric,
    selectedCreatorMetric,
    setSelectedCreatorMetric,
  });

  const { memberTabProps } = useMembersTab({
    queryVariables,
    setSelectedColumns,
    selectedColumns,
    fetching,
    data,
    handleApplyFilters,
  });

  // Add a handler for viewMode changes
  const handleViewModeChange = (newViewMode: ViewMode) => {
    const updatedVariables = {
      ...queryVariables,
      viewMode: newViewMode,
    };
    updateUrlParams(updatedVariables);
  };

  const metaAdAccounts = useMemo(() => {
    return metaAdAccountsData?.client?.metaAdAccounts
      ? metaAdAccountsData.client.metaAdAccounts.filter((a): a is MetaAdAccount => !!a)
      : undefined;
  }, [metaAdAccountsData]);

  // Add instagramAccounts extraction
  const instagramAccounts = useMemo(() => {
    return metaAdAccountsData?.client?.instagramAccounts?.filter((a): a is InstagramAccount => !!a) || [];
  }, [metaAdAccountsData]);

  const { postsTabProps } = usePostsTab({
    queryVariables,
    setSelectedColumns: setSelectedPostColumns,
    selectedColumns: selectedPostColumns,
    fetching: fetchingPosts,
    data: postsData,
    viewMode: (queryVariables.viewMode || DEFAULT_VARIABLES.viewMode) as ViewMode,
    setViewMode: handleViewModeChange,
    dates: {
      startDate: queryVariables.startDate,
      endDate: queryVariables.endDate,
    },
    projects,
    groups,
    updateLocalAssignment: combinedUpdateLocalAssignment,
    metaAdAccounts,
    instagramAccounts,
    postDetails,
  });

  const { networksTabProps } = useNetworksTab({
    queryVariables,
    setSelectedColumns: setSelectedNetworksColumns,
    selectedColumns: selectedNetworksColumns,
    fetching: fetchingNetworks,
    data: networksData ?? {},
  });

  // Update the refresh handler to handle both members and posts
  const handleRefresh = useCallback(() => {
    if (tab === 'summary') {
      handleRefreshSummary();
    } else if (tab === 'members') {
      handleRefreshMembers();
    } else if (tab === 'posts') {
      handleRefreshPosts();
    } else if (tab === 'networks') {
      handleRefreshNetworks();
    }
  }, [tab, handleRefreshSummary, handleRefreshMembers, handleRefreshPosts, handleRefreshNetworks]);

  const handleCancelFilters = () => {
    setIsFiltersOpen(false);
  };

  const handleOpenFilters = () => {
    setIsFiltersOpen(true);
  };

  const handleApplyFiltersAndClose = (formData: z.infer<typeof filtersFormSchema>) => {
    handleApplyFilters(formData);
    memberTabProps.setCurrentPage(0);
    postsTabProps.setCurrentPage(0);
    setIsFiltersOpen(false);
  };

  const handleRemoveFilters = useCallback(
    (type: string) => (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();

      const badgeData: z.infer<typeof filtersFormSchema> = {
        postTypes: queryVariables.postTypes ?? [],
        includeEstimates: queryVariables.includeEstimates,
        networks: queryVariables.networks ?? [],
        projectIds: queryVariables.projectIds ?? [],
        groupIds: queryVariables.groupIds ?? [],
        memberIds: queryVariables.memberIds ?? [],
        filterType: queryVariables?.postTypes?.length ? 'posts' : 'network',
        segments: (queryVariables.segments || []).filter((s: string) => s !== 'unknown') as FiltersFormData['segments'],
        includeAdsData: false,
        postDateIntervalEnabled: queryVariables.postDateIntervalEnabled,
      };

      switch (type) {
        case 'projectIds':
        case 'groupIds':
        case 'memberIds':
        case 'networks':
        case 'postTypes':
          handleApplyFilters({ ...badgeData, [type]: [] });
          break;
        case 'includeEstimates':
          handleApplyFilters({ ...badgeData, includeEstimates: !queryVariables.includeEstimates });
          break;
        case 'segments':
          handleApplyFilters({ ...badgeData, segments: [] });
          break;
        case 'postDateIntervalEnabled':
          handleApplyFilters({ ...badgeData, postDateIntervalEnabled: !queryVariables.postDateIntervalEnabled });
          break;
        default:
          break;
      }
    },
    [handleApplyFilters, queryVariables],
  );

  const handleDateRangeChange = (dateRange: DateRangeFormData) => {
    memberTabProps.setCurrentPage(0);
    postsTabProps.setCurrentPage(0);
    handleDateRangeSubmit(dateRange);
  };

  const headerActions = (
    <>
      <Button
        size="headerIcon"
        variant="outlineHeader"
        disabled={refetching || (tab !== 'summary' && tab !== 'members' && tab !== 'posts' && tab !== 'networks')}
        onClick={handleRefresh}
        data-testid="refresh-button"
        data-dd-action-name="refresh-analytics-data"
        aria-label={refetching ? 'Refreshing data' : 'Refresh data'}
        aria-busy={refetching}
        className="flex items-center justify-center"
      >
        <Spinner size="sm" spinning={refetching} />
      </Button>
      <DateRangeForm
        onApply={handleDateRangeChange}
        defaultValues={{
          dateType: queryVariables.dateType,
          dateRange:
            queryVariables.startDate && queryVariables.endDate
              ? {
                  from: queryVariables.startDate,
                  to: queryVariables.endDate,
                }
              : undefined,
        }}
        minDate={
          clientCreatedDate
            ? (() => {
                const d = new Date(clientCreatedDate);
                d.setUTCHours(0, 0, 0, 0);
                return d;
              })()
            : undefined
        }
        aria-label="Date range selector"
      />
      <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
        <SheetTrigger asChild>
          <Button
            size="headerIcon"
            variant="outlineHeader"
            aria-label="Open filters"
            aria-expanded={isFiltersOpen}
            data-dd-action-name="open-analytics-filters"
          >
            <SlidersIcon aria-hidden="true" />
          </Button>
        </SheetTrigger>
      </Sheet>
    </>
  );

  const isDefaultVariables = useMemo(() => {
    const { clientId: _clientId, endDate: _endDate, startDate: _startDate, ...rest } = queryVariables;
    return isEqual(rest, DEFAULT_VARIABLES);
  }, [queryVariables]);

  const headerTabs = (
    <HeaderTabsList role="tablist" aria-label="Social analytics sections">
      <HeaderTabsTrigger
        value="summary"
        data-action="summary-tab-click"
        data-dd-action-name="summary-tab-click"
        aria-controls="summary-tab"
        role="tab"
        aria-current={tab === 'summary' ? 'page' : undefined}
      >
        Summary
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="posts"
        data-action="posts-tab-click"
        data-dd-action-name="posts-tab-click"
        aria-controls="posts-tab"
        role="tab"
        aria-current={tab === 'posts' ? 'page' : undefined}
      >
        Posts
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="members"
        data-action="members-tab-click"
        data-dd-action-name="members-tab-click"
        aria-controls="members-tab"
        role="tab"
        aria-current={tab === 'members' ? 'page' : undefined}
      >
        Members
      </HeaderTabsTrigger>
      <HeaderTabsTrigger
        value="networks"
        data-action="networks-tab-click"
        data-dd-action-name="networks-tab-click"
        aria-controls="networks-tab"
        role="tab"
        aria-current={tab === 'networks' ? 'page' : undefined}
      >
        Networks
      </HeaderTabsTrigger>
    </HeaderTabsList>
  );

  return (
    <div className="flex h-full flex-col">
      <TooltipProvider>
        <Tabs value={tab} onValueChange={handleTabChange}>
          <AnalyticsHeader actions={headerActions} tabs={headerTabs} />

          <Sheet open={isFiltersOpen} onOpenChange={setIsFiltersOpen} aria-expanded={isFiltersOpen}>
            <SheetContent>
              <SheetHeader className="flex justify-start gap-2 px-6 mt-auto border-b bg-background mx-[-1.5rem]">
                <SheetTitle>Filters</SheetTitle>
              </SheetHeader>
              <FiltersForm
                projects={projects}
                groups={groups}
                members={mappedMembers}
                searchMemberTerm={searchMemberTerm}
                setMemberSearchTerm={setMemberSearchTerm}
                isFetchingProjects={isFetchingProjects}
                isFetchingGroups={isFetchingGroups}
                isFetchingMembers={isFetchingMembers ?? false}
                defaultValues={{
                  ...queryVariables,
                  segments: (queryVariables.segments || []).filter(
                    (s: string) => s !== 'unknown',
                  ) as FiltersFormData['segments'],
                }}
                onSubmit={handleApplyFiltersAndClose}
                onCancel={handleCancelFilters}
              />
            </SheetContent>
          </Sheet>

          <div className="mt-4">
            <div className="grid grid-cols-1 px-4">
              <div className="inline-flex items-center flex-wrap">
                <SocialAnalyticsFilterBadges
                  isDefaultVariables={isDefaultVariables}
                  projectBadgesProps={projectBadgesProps}
                  groupBadgesProps={groupBadgesProps}
                  networkBadgesProps={networkBadgesProps}
                  postTypeBadgesProps={postTypeBadgesProps}
                  includeEstimatesBadgeProps={includeEstimatesBadgeProps}
                  segmentBadgeProps={segmentBadgeProps}
                  memberBadgesProps={memberBadgesProps}
                  postDateIntervalEnabledBadgeProps={postDateIntervalEnabledBadgeProps}
                  onOpenFilters={handleOpenFilters}
                  onRemoveFilters={handleRemoveFilters}
                />
              </div>
            </div>
            <TabsContent
              value="summary"
              id="summary-tab"
              role="tabpanel"
              aria-live="polite"
              aria-busy={fetchingSummary}
            >
              <SummaryTab {...summaryTabProps} />
            </TabsContent>
            <TabsContent value="posts" id="posts-tab" role="tabpanel" aria-live="polite" aria-busy={fetchingPosts}>
              {client && (
                <PostsTab {...postsTabProps} instagramAccounts={instagramAccounts} postDetails={postDetails} />
              )}
            </TabsContent>
            <TabsContent value="members" id="members-tab" role="tabpanel" aria-live="polite" aria-busy={fetching}>
              <MembersTab {...memberTabProps} />
            </TabsContent>
            <TabsContent
              value="networks"
              id="networks-tab"
              role="tabpanel"
              aria-live="polite"
              aria-busy={fetchingNetworks}
            >
              <NetworksTab {...networksTabProps} />
            </TabsContent>
          </div>
        </Tabs>
      </TooltipProvider>

      {/* Status announcer for screen readers */}
      <output aria-live="polite" className="sr-only">
        {fetching && !memberTabProps.isPartialLoading && 'Loading data...'}
        {refetching && 'Refreshing data...'}
        {memberTabProps.isPartialLoading && 'Updating table...'}
      </output>
    </div>
  );
};
